import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowRight, Briefcase, Clock, BookOpen, CheckCircle } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { qualifiantePrograms } from '../../data/programs';
import { formatPrice } from '../../lib/utils';
import SEO from '../../components/SEO';
import CallbackWidget from '../../components/CallbackWidget';

const fadeInUp = {
  initial: { opacity: 0, y: 20 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true },
  transition: { duration: 0.5 }
};

export default function Qualifiante() {
  return (
    <div data-testid="qualifiante-page">
      <SEO
        title="Formation Qualifiante Abidjan - Competences Professionnelles"
        description="Centre de Formation Qualifiante CEFAT : formations professionnelles certifiantes, eligible FDFP. Community Management, Marketing Digital, Comptabilite a Abidjan."
        keywords="formation qualifiante Abidjan, formation professionnelle Cote d'Ivoire, community management Abidjan, marketing digital Abidjan, comptabilite formation Abidjan, CEFAT qualifiante, FDFP"
        url="/qualifiante"
      />
      {/* Hero */}
      <section className="relative bg-[#DC143C] py-20 md:py-32 overflow-hidden">
        <div className="absolute inset-0">
          <div className="absolute top-0 right-0 w-[600px] h-[600px] bg-white/5 rounded-full blur-3xl" />
        </div>
        <div className="max-w-7xl mx-auto px-4 relative z-10">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <motion.div {...fadeInUp}>
              <span className="inline-block bg-white text-[#DC143C] px-4 py-2 rounded-full text-sm font-bold mb-6">
                Centre de Formation Qualifiante
              </span>
              <h1 className="font-heading text-4xl md:text-5xl lg:text-6xl font-black text-white leading-tight mb-6">
                Formations <span className="text-[#FFD700]">Professionnelles</span>
              </h1>
              <p className="text-xl text-white/90 mb-8 leading-relaxed">
                Des formations qualifiantes courtes et intensives pour intégrer rapidement 
                le marché du travail avec des compétences recherchées par les employeurs.
              </p>
              <Link to="/preinscription?center=qualifiante">
                <Button className="bg-white text-[#DC143C] hover:bg-gray-100 rounded-full px-8 py-6 text-lg font-bold shadow-lg">
                  Je me préinscris <ArrowRight className="ml-2" size={20} />
                </Button>
              </Link>
            </motion.div>
            <motion.div 
              initial={{ opacity: 0, x: 50 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="hidden lg:block"
            >
              <img 
                src="https://images.unsplash.com/photo-1758876202167-f81c995c3fdc?w=600&h=500&fit=crop"
                alt="Formation qualifiante"
                className="rounded-2xl shadow-2xl"
              />
            </motion.div>
          </div>
        </div>
      </section>

      {/* Programs Grid */}
      <section className="py-20 bg-[#F8FAFC]" data-testid="programs-section">
        <div className="max-w-7xl mx-auto px-4">
          <motion.div {...fadeInUp} className="text-center mb-16">
            <span className="badge badge-red mb-4">Catalogue</span>
            <h2 className="font-heading text-3xl md:text-4xl font-bold text-[#003366] mb-4">
              Nos Formations Qualifiantes
            </h2>
            <p className="text-gray-600 max-w-2xl mx-auto">
              Des parcours de 9 à 12 mois pour acquérir des compétences professionnelles 
              directement applicables sur le terrain.
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {qualifiantePrograms.map((program, index) => (
              <motion.div
                key={program.id}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.05 }}
                className="bg-white rounded-xl overflow-hidden shadow-md card-hover"
              >
                <div className="h-3 bg-[#DC143C]" />
                <div className="p-6">
                  <div className="flex items-start gap-4 mb-4">
                    <div className="w-12 h-12 bg-[#DC143C]/10 rounded-lg flex items-center justify-center flex-shrink-0">
                      <Briefcase className="text-[#DC143C]" size={24} />
                    </div>
                    <div>
                      <h3 className="font-heading text-lg font-bold text-[#003366]">{program.name}</h3>
                      <div className="flex flex-wrap items-center gap-2 mt-2 text-sm text-gray-500">
                        <span className="flex items-center gap-1"><Clock size={14} /> {program.duration}</span>
                        <span>•</span>
                        <span className="flex items-center gap-1"><BookOpen size={14} /> {program.level}</span>
                      </div>
                    </div>
                  </div>

                  <p className="text-gray-600 text-sm mb-4">{program.description}</p>

                  <div className="bg-[#F8FAFC] rounded-lg p-4 mb-4">
                    <p className="text-sm text-gray-500 mb-1">Coût de la formation</p>
                    <p className="font-heading text-2xl font-bold text-[#003366]">{formatPrice(program.price)}</p>
                  </div>

                  <div className="mb-4">
                    <p className="text-sm font-semibold text-[#003366] mb-2">Débouchés</p>
                    <div className="space-y-1">
                      {program.career.slice(0, 2).map((career, i) => (
                        <p key={i} className="text-sm text-gray-600 flex items-center gap-2">
                          <CheckCircle size={14} className="text-green-600" /> {career}
                        </p>
                      ))}
                    </div>
                  </div>

                  <Link to={`/preinscription?center=qualifiante&program=${program.id}`}>
                    <Button className="w-full bg-[#DC143C] hover:bg-[#B01030] text-white rounded-full">
                      Je m'inscris <ArrowRight size={16} className="ml-2" />
                    </Button>
                  </Link>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Callback Widget */}
      <section className="py-16 bg-white">
        <div className="max-w-5xl mx-auto px-4">
          <CallbackWidget variant="inline" center="qualifiante" />
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 bg-[#DC143C]">
        <div className="max-w-4xl mx-auto px-4 text-center">
          <h2 className="font-heading text-3xl md:text-4xl font-bold text-white mb-6">
            Lancez votre carrière professionnelle
          </h2>
          <p className="text-white/90 mb-8 text-lg">
            Nos formations sont éligibles aux financements FDFP et aux facilités de paiement.
          </p>
          <Link to="/preinscription?center=qualifiante">
            <Button className="bg-white text-[#DC143C] hover:bg-gray-100 rounded-full px-10 py-6 text-lg font-bold shadow-lg">
              Je me préinscris maintenant <ArrowRight className="ml-2" size={20} />
            </Button>
          </Link>
        </div>
      </section>
    </div>
  );
}
